/**
 * New Product Forecast Tab
 *
 * Forecast a new product's 12-month sales using two calculation methods:
 *  1. Benchmark Adjustment: project based on a benchmark SKU with % adjustment
 *  2. Store Profile-Based: estimate volumes by store classification (A/B/C/D doors)
 *     using historical data of an equivalent product, then consolidate
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Grid, TextField, Alert,
  Slider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Snackbar, CircularProgress, Collapse, Chip, ToggleButtonGroup,
  ToggleButton, Checkbox, FormControlLabel, FormGroup, Tooltip,
} from '@mui/material';
import {
  Save, History, ExpandMore, ChevronRight, Delete, TrendingUp,
  StorefrontOutlined,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useForecastingFilters } from './ForecastingFilterContext';
import UnifiedProductSearch, { ProductOption } from '../Common/UnifiedProductSearch';
import { useFullscreen, FullscreenButton } from '../Common/FullscreenButton';
import type { StoreTypeVolume, MonthlyProjectionPoint, StoreProfileForecastResponse } from '../../types/npi';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type CalcMethod = 'benchmark' | 'store_profile';

interface MonthlyRow {
  month: string;
  year: number;
  month_num: number;
  benchmark_sales: number;
  adjusted_forecast: number;
  order_qty: number;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const STORE_TYPES = [
  { value: 'flagship', label: 'Flagship' },
  { value: 'a_door', label: 'A Door' },
  { value: 'b_door', label: 'B Door' },
  { value: 'c_door', label: 'C Door' },
  { value: 'd_door', label: 'D Door' },
  { value: 'standard', label: 'Standard' },
  { value: 'outlet', label: 'Outlet' },
  { value: 'kiosk', label: 'Kiosk' },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function buildForecastRows(
  benchmarkSales: { month: string; year: number; month_num: number; quantity: number }[],
  launchDate: Date,
  adjustmentPct: number,
): MonthlyRow[] {
  const rows: MonthlyRow[] = [];
  const launchMonth = launchDate.getMonth(); // 0-based
  const launchYear = launchDate.getFullYear();

  // Build a lookup from benchmark sales: month_num -> average quantity
  const monthAvg: Record<number, { total: number; count: number }> = {};
  for (const s of benchmarkSales) {
    if (!monthAvg[s.month_num]) monthAvg[s.month_num] = { total: 0, count: 0 };
    monthAvg[s.month_num].total += s.quantity;
    monthAvg[s.month_num].count += 1;
  }

  const multiplier = 1 + adjustmentPct / 100;

  for (let i = 0; i < 12; i++) {
    const m = (launchMonth + i) % 12; // 0-based month
    const y = launchYear + Math.floor((launchMonth + i) / 12);
    const monthNum = m + 1; // 1-based for lookup
    const avg = monthAvg[monthNum] ? monthAvg[monthNum].total / monthAvg[monthNum].count : 0;
    const benchmarkQty = Math.round(avg);
    const adjustedQty = Math.round(avg * multiplier);

    rows.push({
      month: `${MONTH_NAMES[m]} ${y}`,
      year: y,
      month_num: monthNum,
      benchmark_sales: benchmarkQty,
      adjusted_forecast: adjustedQty,
      order_qty: adjustedQty,
    });
  }

  return rows;
}

function formatStoreType(st: string): string {
  const found = STORE_TYPES.find((t) => t.value === st);
  return found ? found.label : st.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const NewProductForecastTab: React.FC = () => {
  const { brands, categories } = useForecastingFilters();
  const queryClient = useQueryClient();
  const { isFullscreen, toggle: toggleFullscreen, FullscreenWrap } = useFullscreen();

  /* Calculation method */
  const [calcMethod, setCalcMethod] = useState<CalcMethod>('benchmark');

  /* New product (single select) */
  const [newProduct, setNewProduct] = useState<ProductOption[]>([]);

  /* Benchmark product (single select) */
  const [benchmarkProduct, setBenchmarkProduct] = useState<ProductOption[]>([]);

  /* Parameters */
  const [adjustmentPct, setAdjustmentPct] = useState(0);
  const [launchDate, setLaunchDate] = useState<Date | null>(new Date());
  const [configName, setConfigName] = useState('');
  const [notes, setNotes] = useState('');

  /* Store profile params */
  const [selectedStoreTypes, setSelectedStoreTypes] = useState<string[]>([]);

  /* Forecast rows (benchmark method) */
  const [forecastRows, setForecastRows] = useState<MonthlyRow[]>([]);
  const [generated, setGenerated] = useState(false);

  /* Store profile results */
  const [storeProfileResult, setStoreProfileResult] = useState<StoreProfileForecastResponse | null>(null);

  /* History panel */
  const [historyOpen, setHistoryOpen] = useState(false);

  /* Notifications */
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  /* Saving */
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  /* ---- Derived ---- */
  const selectedNewProduct = newProduct[0] || null;
  const selectedBenchmark = benchmarkProduct[0] || null;

  /* ---- History query ---- */
  const { data: historyRecords, isLoading: historyLoading } = useQuery({
    queryKey: ['new-product-forecast-history'],
    queryFn: async () => {
      const response = await api.getNewProductForecasts({ is_active: true, limit: 100 });
      return response.data;
    },
    enabled: historyOpen,
  });

  /* ---- Handlers ---- */

  const resetResults = useCallback(() => {
    setGenerated(false);
    setForecastRows([]);
    setStoreProfileResult(null);
  }, []);

  const handleCalcMethodChange = useCallback((_: React.MouseEvent, val: CalcMethod | null) => {
    if (val) {
      setCalcMethod(val);
      resetResults();
    }
  }, [resetResults]);

  const handleNewProductChange = useCallback((products: ProductOption[]) => {
    setNewProduct(products);
    resetResults();
  }, [resetResults]);

  const handleBenchmarkChange = useCallback((products: ProductOption[]) => {
    setBenchmarkProduct(products);
    resetResults();
  }, [resetResults]);

  const handleStoreTypeToggle = useCallback((storeType: string) => {
    setSelectedStoreTypes((prev) =>
      prev.includes(storeType) ? prev.filter((t) => t !== storeType) : [...prev, storeType],
    );
    resetResults();
  }, [resetResults]);

  const handleGeneratePreview = useCallback(async () => {
    if (!selectedBenchmark) {
      setSnackbar({ open: true, message: 'Select a benchmark product first', severity: 'error' });
      return;
    }
    if (calcMethod === 'benchmark' && !selectedNewProduct) {
      setSnackbar({ open: true, message: 'Select a new product first', severity: 'error' });
      return;
    }
    if (calcMethod === 'benchmark' && !launchDate) {
      setSnackbar({ open: true, message: 'Set a launch date', severity: 'error' });
      return;
    }

    setIsGenerating(true);
    try {
      if (calcMethod === 'benchmark') {
        const resp = await api.getBenchmarkMonthlySales(selectedBenchmark.product_id, 12);
        const sales = resp.data?.monthly_sales || [];
        const rows = buildForecastRows(sales, launchDate!, adjustmentPct);
        setForecastRows(rows);
        setStoreProfileResult(null);
      } else {
        const resp = await api.getStoreProfileForecast({
          benchmark_product_id: selectedBenchmark.product_id,
          months: 12,
          adjustment_pct: adjustmentPct,
          target_store_types: selectedStoreTypes.length > 0 ? selectedStoreTypes : null,
        });
        setStoreProfileResult(resp.data);
        setForecastRows([]);
      }
      setGenerated(true);
    } catch (err: any) {
      setSnackbar({ open: true, message: `Failed to generate forecast: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    } finally {
      setIsGenerating(false);
    }
  }, [calcMethod, selectedBenchmark, selectedNewProduct, launchDate, adjustmentPct, selectedStoreTypes]);

  const handleOrderQtyChange = (index: number, qty: number) => {
    setForecastRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, order_qty: qty } : r)),
    );
  };

  const handleSave = async () => {
    if (!selectedBenchmark) {
      setSnackbar({ open: true, message: 'No benchmark selected', severity: 'error' });
      return;
    }

    if (calcMethod === 'benchmark') {
      if (!selectedNewProduct || forecastRows.length === 0) {
        setSnackbar({ open: true, message: 'Generate a forecast preview first', severity: 'error' });
        return;
      }
      if (!launchDate) {
        setSnackbar({ open: true, message: 'Please set a launch date', severity: 'error' });
        return;
      }
    } else {
      if (!storeProfileResult) {
        setSnackbar({ open: true, message: 'Generate a store profile forecast first', severity: 'error' });
        return;
      }
    }

    setIsSaving(true);
    try {
      const bp = selectedBenchmark;

      if (calcMethod === 'benchmark') {
        const np = selectedNewProduct!;
        await api.createNewProductForecast({
          new_product_id: np.product_id,
          new_product_sku: np.sku,
          new_product_name: np.product_name,
          new_product_brand: np.brand,
          new_product_category: np.category,
          benchmark_product_id: bp.product_id,
          benchmark_product_sku: bp.sku,
          benchmark_product_name: bp.product_name,
          benchmark_product_brand: bp.brand,
          benchmark_product_category: bp.category,
          launch_date: launchDate!.toISOString(),
          adjustment_pct: adjustmentPct,
          monthly_forecast: forecastRows.map((r) => ({
            month: r.month,
            benchmark_sales: r.benchmark_sales,
            adjusted_forecast: r.adjusted_forecast,
            order_qty: r.order_qty,
          })),
          config_name: configName || undefined,
          notes: notes || undefined,
        });
      } else {
        const sp = storeProfileResult!;
        const np = selectedNewProduct;
        await api.createNewProductForecast({
          new_product_id: np?.product_id,
          new_product_sku: np?.sku,
          new_product_name: np?.product_name,
          new_product_brand: np?.brand,
          new_product_category: np?.category,
          benchmark_product_id: bp.product_id,
          benchmark_product_sku: bp.sku,
          benchmark_product_name: bp.product_name,
          benchmark_product_brand: bp.brand,
          benchmark_product_category: bp.category,
          launch_date: launchDate?.toISOString() || new Date().toISOString(),
          adjustment_pct: adjustmentPct,
          monthly_forecast: sp.monthly_projection.map((m) => ({
            month: m.month_name,
            benchmark_sales: Math.round(m.projected_quantity / (1 + adjustmentPct / 100) || 0),
            adjusted_forecast: Math.round(m.projected_quantity),
            order_qty: Math.round(m.projected_quantity),
          })),
          config_name: configName ? `[Store Profile] ${configName}` : '[Store Profile] Untitled',
          notes: notes
            ? `${notes}\n\nMethod: Store Profile-Based | Stores: ${sp.total_active_stores} | Annual Est: ${sp.estimated_annual_total.toLocaleString()}`
            : `Method: Store Profile-Based | Stores: ${sp.total_active_stores} | Annual Est: ${sp.estimated_annual_total.toLocaleString()}`,
        });
      }

      setSnackbar({ open: true, message: `Saved forecast "${configName || 'Untitled'}" successfully`, severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['new-product-forecast-history'] });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Save failed: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRecord = async (forecastId: string) => {
    try {
      await api.deleteNewProductForecast(forecastId);
      queryClient.invalidateQueries({ queryKey: ['new-product-forecast-history'] });
      setSnackbar({ open: true, message: 'Forecast record deactivated', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Delete failed: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    }
  };

  /* ---- Totals ---- */
  const totals = useMemo(() => {
    if (forecastRows.length === 0) return null;
    return {
      benchmark: forecastRows.reduce((s, r) => s + r.benchmark_sales, 0),
      forecast: forecastRows.reduce((s, r) => s + r.adjusted_forecast, 0),
      order: forecastRows.reduce((s, r) => s + r.order_qty, 0),
    };
  }, [forecastRows]);

  /* ---- Adjustment marks ---- */
  const adjustmentMarks = [
    { value: -50, label: '-50%' },
    { value: -25, label: '-25%' },
    { value: 0, label: '0%' },
    { value: 50, label: '+50%' },
    { value: 100, label: '+100%' },
  ];

  /* ---- Render ---- */

  return (
    <FullscreenWrap>
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
          New Product Forecast
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined" size="small"
            startIcon={<History sx={{ fontSize: 16 }} />}
            onClick={() => setHistoryOpen(!historyOpen)}
          >
            History
          </Button>
          <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Forecast a new product's 12-month sales using a benchmark SKU as reference.
        Choose a calculation method below.
      </Typography>

      {/* Calculation method toggle */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={calcMethod}
          exclusive
          onChange={handleCalcMethodChange}
          size="small"
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2.5, py: 0.8, fontSize: '13px' } }}
        >
          <ToggleButton value="benchmark">
            <TrendingUp sx={{ fontSize: 16, mr: 0.75 }} />
            Benchmark Adjustment
          </ToggleButton>
          <ToggleButton value="store_profile">
            <StorefrontOutlined sx={{ fontSize: 16, mr: 0.75 }} />
            Store Profile-Based
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          {calcMethod === 'benchmark'
            ? 'Project 12-month sales based on a benchmark SKU with a percentage adjustment.'
            : 'Estimate volumes by store classification (A/B/C/D doors) using historical data of an equivalent product, then consolidate into a projection.'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left column: Product selectors + Parameters */}
        <Grid item xs={12} md={5}>
          {/* New product selector: required for benchmark, optional for store profile */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, color: 'var(--color-text-primary)' }}>
              New Product {calcMethod === 'store_profile' && <Chip label="Optional" size="small" variant="outlined" sx={{ ml: 1, fontSize: '10px', height: 20 }} />}
            </Typography>
            <UnifiedProductSearch
              value={newProduct}
              onChange={handleNewProductChange}
              label="Search new product"
              placeholder="Type SKU or name..."
              singleSelect
              brandFilter={brands}
              categoryFilter={categories}
              size="small"
            />
            {selectedNewProduct && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-sm)', fontSize: '12px' }}>
                <Typography variant="body2"><strong>SKU:</strong> {selectedNewProduct.sku}</Typography>
                <Typography variant="body2"><strong>Name:</strong> {selectedNewProduct.product_name}</Typography>
                {selectedNewProduct.brand && <Typography variant="body2"><strong>Brand:</strong> {selectedNewProduct.brand}</Typography>}
                {selectedNewProduct.category && <Typography variant="body2"><strong>Category:</strong> {selectedNewProduct.category}</Typography>}
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, color: 'var(--color-text-primary)' }}>
              Benchmark SKU (Equivalent Product)
            </Typography>
            <UnifiedProductSearch
              value={benchmarkProduct}
              onChange={handleBenchmarkChange}
              label="Search benchmark product"
              placeholder="Type SKU or name..."
              singleSelect
              brandFilter={brands}
              categoryFilter={categories}
              size="small"
            />
            {selectedBenchmark && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-sm)', fontSize: '12px' }}>
                <Typography variant="body2"><strong>SKU:</strong> {selectedBenchmark.sku}</Typography>
                <Typography variant="body2"><strong>Name:</strong> {selectedBenchmark.product_name}</Typography>
                {selectedBenchmark.brand && <Typography variant="body2"><strong>Brand:</strong> {selectedBenchmark.brand}</Typography>}
                {selectedBenchmark.category && <Typography variant="body2"><strong>Category:</strong> {selectedBenchmark.category}</Typography>}
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, color: 'var(--color-text-primary)' }}>
              Parameters
            </Typography>
            <Grid container spacing={2}>
              {/* Launch date: only for benchmark method */}
              {calcMethod === 'benchmark' && (
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Launch Date"
                      value={launchDate}
                      onChange={(d) => { setLaunchDate(d); resetResults(); }}
                      views={['year', 'month']}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Adjustment vs Benchmark: {adjustmentPct > 0 ? '+' : ''}{adjustmentPct}%
                </Typography>
                <Slider
                  value={adjustmentPct}
                  onChange={(_, v) => { setAdjustmentPct(v as number); resetResults(); }}
                  min={-50} max={100} step={5}
                  marks={adjustmentMarks}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v > 0 ? '+' : ''}${v}%`}
                  sx={{ mx: 1 }}
                />
              </Grid>

              {/* Store type filter: only for store profile method */}
              {calcMethod === 'store_profile' && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Target Store Types
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (leave empty for all)
                    </Typography>
                  </Typography>
                  <FormGroup row sx={{ gap: 0 }}>
                    {STORE_TYPES.map((st) => (
                      <FormControlLabel
                        key={st.value}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedStoreTypes.includes(st.value)}
                            onChange={() => handleStoreTypeToggle(st.value)}
                          />
                        }
                        label={<Typography variant="body2" sx={{ fontSize: '12px' }}>{st.label}</Typography>}
                        sx={{ mr: 1.5 }}
                      />
                    ))}
                  </FormGroup>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Configuration Name"
                  placeholder="e.g. Spring 2026 Launch"
                  value={configName}
                  onChange={(e) => setConfigName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Notes" multiline rows={2}
                  placeholder="Additional context..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained" fullWidth
                  startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : (calcMethod === 'benchmark' ? <TrendingUp /> : <StorefrontOutlined />)}
                  onClick={handleGeneratePreview}
                  disabled={
                    !selectedBenchmark
                    || (calcMethod === 'benchmark' && (!selectedNewProduct || !launchDate))
                    || isGenerating
                  }
                >
                  {isGenerating ? 'Generating...' : 'Generate Preview'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Right column: Results */}
        <Grid item xs={12} md={7}>
          {/* ============================================================ */}
          {/*  BENCHMARK METHOD RESULTS                                     */}
          {/* ============================================================ */}
          {calcMethod === 'benchmark' && (
            <Paper sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                  12-Month Forecast & Order Plan
                </Typography>
                {generated && (
                  <Chip
                    label={`${adjustmentPct > 0 ? '+' : ''}${adjustmentPct}% vs benchmark`}
                    size="small"
                    color={adjustmentPct > 0 ? 'success' : adjustmentPct < 0 ? 'error' : 'default'}
                    variant="outlined"
                    sx={{ fontSize: '11px' }}
                  />
                )}
              </Box>

              {generated && forecastRows.length > 0 ? (
                <>
                  <TableContainer sx={{ maxHeight: 520 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Month</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Benchmark Sales</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Adjusted Forecast</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, fontSize: '11px', width: 100 }}>Order Qty</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {forecastRows.map((row, idx) => (
                          <TableRow key={row.month} hover>
                            <TableCell sx={{ fontSize: '12px', fontWeight: 500 }}>{row.month}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                              {row.benchmark_sales.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                {row.adjusted_forecast.toLocaleString()}
                                {adjustmentPct !== 0 && (
                                  <Chip
                                    label={`${adjustmentPct > 0 ? '+' : ''}${adjustmentPct}%`}
                                    size="small"
                                    color={adjustmentPct > 0 ? 'success' : 'error'}
                                    variant="outlined"
                                    sx={{ fontSize: '9px', height: 18, ml: 0.5 }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number" size="small"
                                value={row.order_qty}
                                onChange={(e) => handleOrderQtyChange(idx, parseInt(e.target.value) || 0)}
                                inputProps={{ min: 0, style: { textAlign: 'center', width: 60, fontSize: '12px', padding: '4px' } }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* Totals row */}
                        {totals && (
                          <TableRow sx={{ bgcolor: 'var(--color-bg-secondary)' }}>
                            <TableCell sx={{ fontSize: '12px', fontWeight: 700 }}>Total (12M)</TableCell>
                            <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace' }}>
                              {totals.benchmark.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace' }}>
                              {totals.forecast.toLocaleString()}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace' }}>
                              {totals.order.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {totals && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <strong>Summary:</strong> 12-month adjusted forecast totals{' '}
                      <strong>{totals.forecast.toLocaleString()}</strong> units
                      ({adjustmentPct > 0 ? '+' : ''}{adjustmentPct}% vs benchmark {totals.benchmark.toLocaleString()}).
                      Order plan: <strong>{totals.order.toLocaleString()}</strong> units.
                    </Alert>
                  )}
                </>
              ) : (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <TrendingUp sx={{ fontSize: 48, color: 'var(--color-text-muted)', mb: 1 }} />
                  <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
                    Select a new product, benchmark SKU, set parameters, and click "Generate Preview"
                    to see the 12-month forecast and order plan.
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* ============================================================ */}
          {/*  STORE PROFILE METHOD RESULTS                                 */}
          {/* ============================================================ */}
          {calcMethod === 'store_profile' && (
            <>
              {generated && storeProfileResult ? (
                <>
                  {/* Store Type Breakdown */}
                  <Paper sx={{ p: 3, mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                        Volume by Store Classification
                      </Typography>
                      <Chip
                        label={`${storeProfileResult.total_active_stores} stores`}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ fontSize: '11px' }}
                      />
                    </Box>

                    <TableContainer sx={{ maxHeight: 320 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Store Type</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Active Stores</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>
                              <Tooltip title="Average monthly volume for this store type from the benchmark product">
                                <span>Benchmark Avg/Mo</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>
                              <Tooltip title="Average monthly units per individual store from the benchmark product">
                                <span>Avg/Store/Mo</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>
                              <Tooltip title="Estimated monthly units per store after adjustment">
                                <span>Est/Store/Mo</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>
                              <Tooltip title="Total estimated monthly volume for this store type (est/store x active stores)">
                                <span>Est Monthly Total</span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {storeProfileResult.store_type_breakdown.map((row: StoreTypeVolume) => (
                            <TableRow key={row.store_type} hover>
                              <TableCell sx={{ fontSize: '12px', fontWeight: 500 }}>
                                <Chip
                                  label={formatStoreType(row.store_type)}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '11px', height: 22 }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                                {row.active_stores}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                                {row.benchmark_avg_monthly_volume.toLocaleString()}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                                {row.benchmark_avg_monthly_per_store.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                                {row.estimated_monthly_per_store.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                                {adjustmentPct !== 0 && (
                                  <Chip
                                    label={`${adjustmentPct > 0 ? '+' : ''}${adjustmentPct}%`}
                                    size="small"
                                    color={adjustmentPct > 0 ? 'success' : 'error'}
                                    variant="outlined"
                                    sx={{ fontSize: '8px', height: 16, ml: 0.5 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 600, fontFamily: 'monospace' }}>
                                {row.estimated_monthly_total.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}

                          {/* Totals row */}
                          <TableRow sx={{ bgcolor: 'var(--color-bg-secondary)' }}>
                            <TableCell sx={{ fontSize: '12px', fontWeight: 700 }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace' }}>
                              {storeProfileResult.total_active_stores}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace' }}>
                              {storeProfileResult.store_type_breakdown.reduce((s, r) => s + r.benchmark_avg_monthly_volume, 0).toLocaleString()}
                            </TableCell>
                            <TableCell colSpan={2} />
                            <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace' }}>
                              {storeProfileResult.estimated_monthly_total.toLocaleString()}/mo
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  {/* 12-Month Consolidated Projection */}
                  <Paper sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                        12-Month Consolidated Projection
                      </Typography>
                      <Chip
                        label={`Annual: ${storeProfileResult.estimated_annual_total.toLocaleString()} units`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '11px' }}
                      />
                    </Box>

                    <TableContainer sx={{ maxHeight: 420 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Month</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Seasonal Weight</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Projected Quantity</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {storeProfileResult.monthly_projection.map((row: MonthlyProjectionPoint) => (
                            <TableRow key={row.month} hover>
                              <TableCell sx={{ fontSize: '12px', fontWeight: 500 }}>{row.month_name}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                                {(row.seasonal_weight * 100).toFixed(1)}%
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 600, fontFamily: 'monospace' }}>
                                {row.projected_quantity.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}

                          {/* Totals row */}
                          <TableRow sx={{ bgcolor: 'var(--color-bg-secondary)' }}>
                            <TableCell sx={{ fontSize: '12px', fontWeight: 700 }}>Total (12M)</TableCell>
                            <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace' }}>
                              100.0%
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace' }}>
                              {storeProfileResult.estimated_annual_total.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Alert severity="info" sx={{ mt: 2 }}>
                      <strong>Summary:</strong> Based on{' '}
                      <strong>{storeProfileResult.benchmark_product_name || storeProfileResult.benchmark_product_sku}</strong> historical sales across{' '}
                      <strong>{storeProfileResult.store_type_breakdown.length}</strong> store classifications ({storeProfileResult.total_active_stores} total stores),
                      the estimated annual volume is <strong>{storeProfileResult.estimated_annual_total.toLocaleString()}</strong> units
                      ({storeProfileResult.estimated_monthly_total.toLocaleString()}/month)
                      {adjustmentPct !== 0 && <> with a <strong>{adjustmentPct > 0 ? '+' : ''}{adjustmentPct}%</strong> adjustment</>}.
                    </Alert>
                  </Paper>
                </>
              ) : (
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ py: 8, textAlign: 'center' }}>
                    <StorefrontOutlined sx={{ fontSize: 48, color: 'var(--color-text-muted)', mb: 1 }} />
                    <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
                      Select a benchmark SKU (equivalent product), optionally filter store types,
                      set the adjustment percentage, and click "Generate Preview" to see volume estimates
                      by store classification and the consolidated 12-month projection.
                    </Typography>
                  </Box>
                </Paper>
              )}
            </>
          )}

          {/* Save bar */}
          {generated && (forecastRows.length > 0 || storeProfileResult) && (
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained" color="success"
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Forecast'}
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* History section */}
      <Box sx={{ mt: 4 }}>
        <Box
          onClick={() => setHistoryOpen(!historyOpen)}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mb: historyOpen ? 2 : 0, userSelect: 'none' }}
        >
          <IconButton size="small" sx={{ p: 0 }}>
            {historyOpen ? <ExpandMore sx={{ fontSize: 20 }} /> : <ChevronRight sx={{ fontSize: 20 }} />}
          </IconButton>
          <Typography sx={{ fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
            Forecast History
          </Typography>
        </Box>
        <Collapse in={historyOpen}>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
          ) : historyRecords && historyRecords.length > 0 ? (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>New Product</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Benchmark</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '11px' }}>Adjustment</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Launch Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>12M Forecast</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>12M Order</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '11px', width: 50 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyRecords.map((rec: any) => {
                    const totalForecast = rec.monthly_forecast
                      ? rec.monthly_forecast.reduce((s: number, m: any) => s + (m.adjusted_forecast || 0), 0)
                      : 0;
                    const totalOrder = rec.monthly_forecast
                      ? rec.monthly_forecast.reduce((s: number, m: any) => s + (m.order_qty || 0), 0)
                      : 0;
                    const isStoreProfile = rec.config_name?.startsWith('[Store Profile]');
                    return (
                      <TableRow key={rec.forecast_id} hover>
                        <TableCell sx={{ fontSize: '12px' }}>
                          {rec.config_name || '\u2014'}
                          {isStoreProfile && (
                            <Chip label="Store Profile" size="small" sx={{ ml: 0.5, fontSize: '9px', height: 18 }} color="secondary" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>
                          {rec.new_product_sku || rec.new_product_name || '\u2014'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>
                          {rec.benchmark_product_sku || rec.benchmark_product_name || '\u2014'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${rec.adjustment_pct > 0 ? '+' : ''}${rec.adjustment_pct}%`}
                            size="small"
                            color={rec.adjustment_pct > 0 ? 'success' : rec.adjustment_pct < 0 ? 'error' : 'default'}
                            variant="outlined"
                            sx={{ fontSize: '11px', height: 22 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>
                          {rec.launch_date ? new Date(rec.launch_date).toLocaleDateString() : '\u2014'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                          {totalForecast.toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                          {totalOrder.toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small" color="error"
                            onClick={() => handleDeleteRecord(rec.forecast_id)}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary" textAlign="center" sx={{ py: 3, fontSize: '13px' }}>
              No forecast records found.
            </Typography>
          )}
        </Collapse>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </FullscreenWrap>
  );
};

export default NewProductForecastTab;
